<template>
  <div>
    <!-- Fica dentro de um passo do assistente de migração, e não direto no
         v-card do diálogo: por isso corpo e rodapé repetem localmente o visual
         de .ds-dialog-body / .ds-dialog-actions (que só alcançam filhos diretos). -->
    <v-card-text class="clinic-form-body">
      <v-form ref="form" v-model="valid" lazy-validation>
        <!-- Informações básicas -->
        <section class="ds-form-section" data-tour="cf-info">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-information-outline</v-icon>
              <span>Informações Básicas</span>
            </div>
          </div>
          <v-row dense>
            <v-col cols="12" md="7">
              <v-text-field
                v-model="form.name"
                label="Nome da Clínica *"
                :rules="nameRules"
                required
                clearable
                outlined
                dense
                hide-details="auto"
                color="primary"
                prepend-inner-icon="mdi-hospital-building"
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field
                v-model="form.cnpj"
                label="CNPJ *"
                :rules="cnpjRules"
                v-mask="'##.###.###/####-##'"
                required
                clearable
                outlined
                dense
                hide-details="auto"
                color="primary"
                prepend-inner-icon="mdi-card-account-details"
              />
            </v-col>
          </v-row>
        </section>

        <!-- Endereço -->
        <section class="ds-form-section" data-tour="cf-endereco">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-map-marker-outline</v-icon>
              <span>Endereço</span>
            </div>
          </div>
          <v-progress-linear
            v-if="loadingCEP"
            indeterminate
            color="primary"
            class="mb-3"
          ></v-progress-linear>

          <v-row dense>
            <v-col cols="12" md="3" data-tour="cf-cep">
              <v-text-field
                v-model="form.cep"
                label="CEP *"
                :rules="cepRules"
                v-mask="'#####-###'"
                @blur="fetchAddressByCep"
                required
                clearable
                outlined
                dense
                hide-details="auto"
                color="primary"
                prepend-inner-icon="mdi-map-search-outline"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.street"
                label="Logradouro *"
                :rules="[(v) => !!v || 'Logradouro é obrigatório']"
                required
                clearable
                outlined
                dense
                hide-details="auto"
                color="primary"
                prepend-inner-icon="mdi-road-variant"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="form.number"
                label="Número *"
                :rules="[(v) => !!v || 'Número é obrigatório']"
                required
                clearable
                outlined
                dense
                hide-details="auto"
                color="primary"
                prepend-inner-icon="mdi-numeric"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="form.complement"
                label="Complemento"
                clearable
                outlined
                dense
                hide-details="auto"
                color="primary"
                prepend-inner-icon="mdi-office-building-marker-outline"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="form.neighborhood"
                label="Bairro *"
                :rules="[(v) => !!v || 'Bairro é obrigatório']"
                required
                clearable
                outlined
                dense
                hide-details="auto"
                color="primary"
                prepend-inner-icon="mdi-home-city-outline"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="form.city"
                label="Cidade *"
                :rules="[(v) => !!v || 'Cidade é obrigatória']"
                required
                clearable
                outlined
                dense
                hide-details="auto"
                color="primary"
                prepend-inner-icon="mdi-city"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field
                v-model="form.state"
                label="Estado *"
                :rules="[(v) => !!v || 'Estado é obrigatório']"
                required
                clearable
                outlined
                dense
                hide-details="auto"
                color="primary"
                prepend-inner-icon="mdi-map"
              />
            </v-col>
          </v-row>
        </section>

        <!-- Telefones -->
        <section class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-phone-outline</v-icon>
              <span>Telefones</span>
            </div>
            <v-btn
              small
              outlined
              color="primary"
              @click="addPhone"
              :disabled="form.phones.length >= 5"
            >
              <v-icon small left>mdi-plus</v-icon>
              Adicionar
            </v-btn>
          </div>

          <div v-if="!form.phones.length" class="text-center pa-2 grey--text">
            <v-icon class="mb-1">mdi-phone-off</v-icon>
            <p class="mb-0">Nenhum telefone cadastrado</p>
          </div>

          <div v-for="(phone, index) in form.phones" :key="index" class="mb-2">
            <v-row dense align="center">
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="phone.number"
                  label="Telefone *"
                  :rules="phoneRules"
                  v-mask="['(##) ####-####', '(##) #####-####']"
                  clearable
                  outlined
                  dense
                  hide-details="auto"
                  color="primary"
                  required
                  prepend-inner-icon="mdi-phone"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="phone.type"
                  :items="phoneTypes"
                  label="Tipo *"
                  :rules="[(v) => !!v || 'Tipo de telefone é obrigatório']"
                  required
                  clearable
                  outlined
                  dense
                  hide-details="auto"
                  color="primary"
                />
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-checkbox
                  v-model="phone.is_primary"
                  @change="handlePrimaryChange(index)"
                  label="WhatsApp"
                  color="primary"
                  dense
                  hide-details
                  class="mt-0 pt-0"
                />
              </v-col>

              <v-col cols="12" md="1" class="d-flex align-center">
                <v-btn
                  icon
                  small
                  color="error"
                  @click="removePhone(index)"
                  :disabled="form.phones.length === 1"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </section>
      </v-form>
    </v-card-text>

    <v-card-actions class="clinic-form-actions">
      <v-btn
        outlined
        color="primary"
        @click="$emit('close')"
        :disabled="loading"
      >
        <v-icon left>mdi-close</v-icon>
        Cancelar
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        depressed
        @click="save"
        :loading="loading"
        :disabled="!valid || !form.name"
        data-tour="cf-salvar"
      >
        <v-icon left>mdi-content-save</v-icon>
        Salvar
      </v-btn>
    </v-card-actions>
  </div>
</template>

<script>
import http from "@/services/http";
import { buscarEnderecoPorCep } from "@/utils/buscarEnderecoPorCep.js";
import { useSnackbar } from "@/composables/useSnackbar";

export default {
  name: "ClinicForm",

  props: {
    isEdit: { type: Boolean, default: false },
    clinicId: { type: [String, Number], default: null },
    migrationData: { type: Object, default: () => ({}) },
  },

  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },

  data() {
    return {
      valid: false,
      loading: false,
      loadingCEP: false,

      form: {
        name: "",
        cnpj: "",
        cep: "",
        street: "",
        number: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "",
        phones: [],
      },

      phoneTypes: [
        { text: "Celular", value: "celular" },
        { text: "Fixo", value: "fixo" },
      ],

      nameRules: [
        (v) => !!v || "Nome da clínica é obrigatório",
        (v) => (v && v.length >= 3) || "Nome deve ter pelo menos 3 caracteres",
      ],

      cnpjRules: [
        (v) => !!v || "CNPJ é obrigatório",
        (v) => !v || this.validateCNPJ(v) || "CNPJ inválido",
      ],

      cepRules: [
        (v) => !!v || "CEP é obrigatório",
        (v) =>
          !v || v.replace(/\D/g, "").length === 8 || "CEP deve ter 8 dígitos",
      ],

      phoneRules: [
        (v) => !!v || "Telefone é obrigatório",
        (v) =>
          !v ||
          v.replace(/\D/g, "").length >= 10 ||
          "Telefone deve ter pelo menos 10 dígitos",
      ],
    };
  },

  watch: {
    clinicId: {
      async handler(newVal) {
        if (this.isEdit && newVal) {
          await this.loadClinicData();
        }
      },
      immediate: true,
    },

    isEdit: {
      async handler(newVal) {
        if (newVal && this.clinicId) {
          await this.loadClinicData();
        } else if (!newVal) {
          this.resetForm();
          if (!this.form.phones.length) this.addPhone();
        }
      },
      immediate: true,
    },
  },

  mounted() {
    // Só garante que em modo criação tenha pelo menos 1 telefone
    if (!this.isEdit && !this.form.phones.length) {
      this.addPhone();
    }
  },

  methods: {
    buscarEnderecoPorCep,

    async loadClinicData() {
      try {
        this.loading = true;

        const { data: clinic } = await http.get(
          `/api/clinics/get-clinic/${this.clinicId}`,
        );

        this.form = {
          name: clinic.name || "",
          cnpj: clinic.cnpj || "",
          cep: clinic.cep || "",
          street: clinic.street || "",
          number: clinic.number || "",
          complement: clinic.complement || "",
          neighborhood: clinic.neighborhood || "",
          city: clinic.city || "",
          state: clinic.state || "",
          phones:
            clinic.phones?.map((phone) => ({
              id: phone.id,
              number: phone.number || "",
              type: phone.type || "celular",
              is_primary: !!phone.is_primary,
            })) || [],
        };

        if (!this.form.phones.length) this.addPhone();
      } catch (err) {
        console.error("Erro ao carregar clínica:", err);
        this.erroDeRequisicao(err, "Erro ao carregar dados da clínica");
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        name: "",
        cnpj: "",
        cep: "",
        street: "",
        number: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "",
        phones: [],
      };
      this.valid = false;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    addPhone() {
      if (this.form.phones.length >= 3) return;

      this.form.phones.push({
        number: "",
        type: "celular",
        is_primary: this.form.phones.length === 0,
      });
    },

    removePhone(index) {
      if (this.form.phones.length <= 1) return;

      const removedPhone = this.form.phones[index];
      this.form.phones.splice(index, 1);

      if (removedPhone.is_primary && this.form.phones.length > 0) {
        this.form.phones[0].is_primary = true;
      }
    },

    handlePrimaryChange(index) {
      if (this.form.phones[index].is_primary) {
        this.form.phones.forEach((phone, i) => {
          if (i !== index) phone.is_primary = false;
        });
      } else {
        const hasPrimary = this.form.phones.some((p) => p.is_primary);
        if (!hasPrimary && this.form.phones.length > 0) {
          this.form.phones[0].is_primary = true;
        }
      }
    },

    async fetchAddressByCep() {
      const cep = this.form.cep;

      this.loadingCEP = true;
      try {
        if (!cep || cep.replace(/\D/g, "").length === 0) {
          this.form.street = "";
          this.form.neighborhood = "";
          this.form.city = "";
          this.form.state = "";
          this.form.number = "";
          return;
        }

        const endereco = await buscarEnderecoPorCep(cep);

        if (endereco) {
          this.form.street = endereco.street || "";
          this.form.neighborhood = endereco.neighborhood || "";
          this.form.city = endereco.city || "";
          this.form.state = endereco.state || "";
        }
      } finally {
        // ✅ garante desligar sempre
        this.loadingCEP = false;
      }
    },

    validateCNPJ(cnpj) {
      if (!cnpj) return true;
      const clean = cnpj.replace(/\D/g, "");
      if (clean.length !== 14) return false;
      if (/^(\d)\1+$/.test(clean)) return false;
      return true;
    },

    async save() {
      if (!this.$refs.form.validate()) return;

      if (!this.form.name.trim()) {
        this.error("Nome da clínica é obrigatório");
        return;
      }

      if (!this.form.phones.length) {
        this.error("Pelo menos um telefone é obrigatório");
        return;
      }

      const invalidPhone = this.form.phones.find((phone) => {
        const cleaned = (phone.number || "").replace(/\D/g, "");
        return !cleaned || cleaned.length < 10;
      });

      if (invalidPhone) {
        this.error("Todos os telefones devem ser válidos");
        return;
      }

      if (!this.form.phones.some((p) => p.is_primary)) {
        this.form.phones[0].is_primary = true;
      }

      this.loading = true;

      try {
        let payload = { ...this.form };

        if (!this.isEdit && this.migrationData) {
          payload = { ...payload, ...this.migrationData };
        }

        if (this.isEdit) {
          await http.put(`/api/clinics/edit-clinic/${this.clinicId}`, payload);
        } else {
          await http.post("/api/clinics/user-create-clinic", payload);
        }

        // ✅ Atualiza user + user_type no Vuex/localStorage
        await this.$store.dispatch("auth/refreshMe");

        this.$emit("clinic-saved");
      } catch (err) {
        console.error("Erro ao salvar clínica:", err);
        const message = err.response?.data?.message || "Erro ao salvar clínica";
        this.erroDeRequisicao(err, message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Mesmo visual de .ds-dialog-body / .ds-dialog-actions (salvia-dialogs.css),
   aplicado aqui porque o formulário não é filho direto do v-card do diálogo. */
.clinic-form-body {
  padding: 12px 14px !important;
  background-color: var(--ds-blue-bg-soft);
}
.clinic-form-actions {
  padding: 10px 14px !important;
  background-color: var(--ds-blue-50);
  border-top: 1px solid var(--ds-blue-100);
}

/* ═══ Botão "Adicionar Telefone" ═══ */
:deep(.v-btn.primary) {
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
  box-shadow: 0 2px 6px rgba(31, 77, 63, 0.18) !important;
}

/* ═══ Empty state ═══ */
.grey--text {
  color: var(--ds-text-muted) !important;
}
</style>
